// batch-68 gespreksvoorbereiding (FO-346 §3.7) - ontwikkeldoelen en doorgroeiwens
// van een medewerker. Vastgelegd in het dossier (tab Ontwikkeling) of vanuit het
// verslagformulier van een gesprek (bronGesprekId). Niet-interne doelen ziet de
// medewerker zelf in de portal (Mijn opleidingen > Mijn ontwikkeling).
//
// batch-71 gesprek-secties (FO-536) - de sectie Ontwikkeling in het verslag schrijft
// direct naar deze doelen (één bron): de doorgroeiwens krijgt richting, termijn en een
// interne inschatting, een doel kan aan een skill en een opleiding hangen.

import { z } from 'zod'
import { queryBoolean } from './zod-query'

export const ONTWIKKEL_DOEL_SOORTEN = ['DOEL', 'DOORGROEIWENS'] as const
export type OntwikkelDoelSoort = (typeof ONTWIKKEL_DOEL_SOORTEN)[number]

export const ONTWIKKEL_DOEL_SOORT_LABELS: Record<OntwikkelDoelSoort, string> = {
  DOEL: 'Ontwikkeldoel',
  DOORGROEIWENS: 'Doorgroeiwens',
}

export const ONTWIKKEL_DOEL_STATUSSEN = ['OPEN', 'BEZIG', 'BEHAALD', 'VERVALLEN'] as const
export type OntwikkelDoelStatus = (typeof ONTWIKKEL_DOEL_STATUSSEN)[number]

export const ONTWIKKEL_DOEL_STATUS_LABELS: Record<OntwikkelDoelStatus, string> = {
  OPEN: 'Open',
  BEZIG: 'Bezig',
  BEHAALD: 'Behaald',
  VERVALLEN: 'Vervallen',
}

// ─── batch-71 gesprek-secties (FO-536) - doorgroeiwens en sectie per gesprekstype ─

/** De sectie in het verslagformulier per gesprekstype (ook de controlelijst-sectie). */
export const GESPREK_SECTIE_MODI = ['VERBORGEN', 'OPTIONEEL', 'VERPLICHT'] as const
export type GesprekSectieModus = (typeof GESPREK_SECTIE_MODI)[number]

export const GESPREK_SECTIE_MODUS_LABELS: Record<GesprekSectieModus, string> = {
  VERBORGEN: 'Verborgen',
  OPTIONEEL: 'Optioneel',
  VERPLICHT: 'Verplicht',
}

export const ONTWIKKEL_DOEL_TERMIJNEN = ['KORTER_DAN_1_JAAR', 'EEN_TOT_3_JAAR', 'LANGER_DAN_3_JAAR', 'GEEN_WENS'] as const
export type OntwikkelDoelTermijn = (typeof ONTWIKKEL_DOEL_TERMIJNEN)[number]

export const ONTWIKKEL_DOEL_TERMIJN_LABELS: Record<OntwikkelDoelTermijn, string> = {
  KORTER_DAN_1_JAAR: 'Korter dan 1 jaar',
  EEN_TOT_3_JAAR: '1-3 jaar',
  LANGER_DAN_3_JAAR: 'Langer dan 3 jaar',
  GEEN_WENS: 'Geen wens op dit moment',
}

/** INTERN (spec 236 A3): nooit in de portal, de PDF of het feitenoverzicht. */
export const ONTWIKKEL_DOEL_INSCHATTINGEN = ['REALISTISCH_NU', 'MET_ONTWIKKELING', 'NIET_PASSEND'] as const
export type OntwikkelDoelInschatting = (typeof ONTWIKKEL_DOEL_INSCHATTINGEN)[number]

export const ONTWIKKEL_DOEL_INSCHATTING_LABELS: Record<OntwikkelDoelInschatting, string> = {
  REALISTISCH_NU: 'Realistisch nu',
  MET_ONTWIKKELING: 'Met ontwikkeling',
  NIET_PASSEND: 'Niet passend',
}

export const ONTWIKKEL_DOEL_EIGENAAR_SOORTEN = ['MEDEWERKER', 'LEIDINGGEVENDE'] as const
export type OntwikkelDoelEigenaarSoort = (typeof ONTWIKKEL_DOEL_EIGENAAR_SOORTEN)[number]

export const ONTWIKKEL_DOEL_EIGENAAR_SOORT_LABELS: Record<OntwikkelDoelEigenaarSoort, string> = {
  MEDEWERKER: 'Medewerker',
  LEIDINGGEVENDE: 'Leidinggevende',
}

/** Na zoveel maanden zonder bespreking telt een open doel als "zonder opvolging" (FO-536 §3.B). */
export const ONTWIKKEL_ZONDER_OPVOLGING_MAANDEN = 6

/** Een melding over de streefdatum komt zoveel dagen vooraf (FO-536 §7). */
export const ONTWIKKEL_STREEFDATUM_MELDING_DAGEN = 30

const YMD = /^\d{4}-\d{2}-\d{2}$/
const OPTIONELE_TEKST = (max: number) => z.string().trim().max(max, `Maximaal ${max} tekens`).nullable().optional()
const KALE_ID = z.string().trim().min(1).max(64)

/** Velden die een doel in een gesprek krijgt, bovenop de basis (create en update). */
const DOEL_KOPPELINGEN = {
  eigenaarSoort: z.enum(ONTWIKKEL_DOEL_EIGENAAR_SOORTEN).nullable().optional(),
  skillId: KALE_ID.nullable().optional(),
  /** Doel-niveau op de schaal van de skill (0-10, de API toetst de schaal van de skill). */
  doelNiveau: z.number().int().min(0, 'Minimaal 0').max(10, 'Maximaal 10').nullable().optional(),
  /** Het gesprek waarin dit gebeurt: de wijziging komt in de audit gekoppeld aan dat gesprek. */
  gesprekId: KALE_ID.nullable().optional(),
}

/** Nieuw doel of nieuwe doorgroeiwens. */
export const ontwikkelDoelCreateSchema = z.object({
  soort: z.enum(ONTWIKKEL_DOEL_SOORTEN).default('DOEL'),
  omschrijving: z
    .string()
    .trim()
    .min(1, 'Beschrijf het doel of de wens')
    .max(2000, 'Maximaal 2000 tekens'),
  streefdatum: z.string().regex(YMD, 'Kies een geldige datum').nullable().optional(),
  status: z.enum(ONTWIKKEL_DOEL_STATUSSEN).optional(),
  bronGesprekId: z.string().trim().min(1).max(64).nullable().optional(),
  intern: z.boolean().optional(),
  ...DOEL_KOPPELINGEN,
  /** Maakt direct een opleidingsaanvraag met het doel als motivatie, of koppelt aan een lopende (spec 236 §6.2). */
  opleidingId: KALE_ID.nullable().optional(),
})
export type OntwikkelDoelCreateInput = z.infer<typeof ontwikkelDoelCreateSchema>

/**
 * Wijzigen. Accepteert de eigen read-DTO terug (nullable streefdatum en
 * bron-gesprek zijn legitieme leeg-waarden); onbekende velden negeert zod.
 */
export const ontwikkelDoelUpdateSchema = z.object({
  soort: z.enum(ONTWIKKEL_DOEL_SOORTEN).optional(),
  omschrijving: z
    .string()
    .trim()
    .min(1, 'Beschrijf het doel of de wens')
    .max(2000, 'Maximaal 2000 tekens')
    .optional(),
  streefdatum: z.string().regex(YMD, 'Kies een geldige datum').nullable().optional(),
  status: z.enum(ONTWIKKEL_DOEL_STATUSSEN).optional(),
  bronGesprekId: z.string().trim().min(1).max(64).nullable().optional(),
  intern: z.boolean().optional(),
  ...DOEL_KOPPELINGEN,
  /** Toelichting bij de statuswijziging (statusknoppen in het verslag). */
  statusToelichting: OPTIONELE_TEKST(2000),
  /** Koppelt aan een opleiding: een lopende aanvraag, anders een nieuwe (spec 236 §6.2). */
  opleidingId: KALE_ID.nullable().optional(),
})
export type OntwikkelDoelUpdateInput = z.infer<typeof ontwikkelDoelUpdateSchema>

// ─── De doorgroeiwens in het gesprek (FO-536 §3.A2) ──────────────────────────

/** "Teamleider, 1-3 jaar" of "Geen wens op dit moment": de samenvatting op de rij. */
export function doorgroeiwensOmschrijving(richting: string | null | undefined, termijn: OntwikkelDoelTermijn | null | undefined): string {
  if (termijn === 'GEEN_WENS') return ONTWIKKEL_DOEL_TERMIJN_LABELS.GEEN_WENS
  const r = richting?.trim()
  if (r && termijn) return `${r}, ${ONTWIKKEL_DOEL_TERMIJN_LABELS[termijn].toLowerCase()}`
  return r ?? (termijn ? ONTWIKKEL_DOEL_TERMIJN_LABELS[termijn] : '')
}

/**
 * De wens bij een gesprek. `AANGEPAST` (of een eerste wens) maakt een nieuwe rij en
 * zet de vorige op VERVALLEN met `vervangenDoorId`; `ONGEWIJZIGD` legt alleen vast dat
 * de bestaande wens in dit gesprek is besproken.
 */
export const gesprekWensSchema = z.discriminatedUnion('actie', [
  z.object({ actie: z.literal('ONGEWIJZIGD') }),
  z
    .object({
      actie: z.literal('AANGEPAST'),
      richting: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
      termijn: z.enum(ONTWIKKEL_DOEL_TERMIJNEN, { error: 'Kies een termijn' }),
      toelichtingMedewerker: OPTIONELE_TEKST(2000),
      inschattingLeidinggevende: z.enum(ONTWIKKEL_DOEL_INSCHATTINGEN).nullable().optional(),
      inschattingToelichting: OPTIONELE_TEKST(2000),
    })
    .superRefine((v, ctx) => {
      if (v.termijn !== 'GEEN_WENS' && !v.richting?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['richting'], message: 'Kies of beschrijf de richting' })
      }
    }),
])
export type GesprekWensInput = z.infer<typeof gesprekWensSchema>

/**
 * Vrijgeefregel van de sectie (FO-536 §3.A1, §6.1). Eén regel voor de API en het form:
 * bij VERPLICHT moet de doorgroeiwens (of expliciet "geen wens") in dit gesprek zijn
 * vastgelegd of bevestigd. Bij OPTIONEEL mag alles leeg blijven.
 */
export function valideerOntwikkelSectie(input: {
  modus: GesprekSectieModus
  wensBesprokenInGesprek: boolean
}): { veld: string; message: string }[] {
  if (input.modus !== 'VERPLICHT' || input.wensBesprokenInGesprek) return []
  return [
    {
      veld: 'ontwikkeling',
      message: 'Dit gesprekstype vraagt om de doorgroeiwens: leg de wens vast of kies "geen wens op dit moment"',
    },
  ]
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Een gesprek waarin een doel of wens is besproken. */
export interface OntwikkelBesprekingDTO {
  gesprekId: string
  titel: string
  /** NL-kalenderdag yyyy-mm-dd. */
  datum: string
}

export interface OntwikkelDoelDTO {
  id: string
  employeeId: string
  soort: OntwikkelDoelSoort
  omschrijving: string
  /** NL-kalenderdag yyyy-mm-dd of null. */
  streefdatum: string | null
  status: OntwikkelDoelStatus
  bronGesprekId: string | null
  /** Titel van het bron-gesprek (neutraal bij een verzuimgesprek zonder recht). */
  bronGesprekTitel: string | null
  eigenaarNaam: string | null
  intern: boolean
  createdAt: string
  updatedAt: string
  // ── batch-71 gesprek-secties (FO-536) ──
  richting: string | null
  termijn: OntwikkelDoelTermijn | null
  toelichtingMedewerker: string | null
  /** INTERN: alleen met MEDEWERKERS.canEdit, HR of als teamleider van het team; anders null. */
  inschattingLeidinggevende: OntwikkelDoelInschatting | null
  inschattingToelichting: string | null
  eigenaarSoort: OntwikkelDoelEigenaarSoort | null
  skillId: string | null
  skillNaam: string | null
  doelNiveau: number | null
  /** Het effectieve niveau van de medewerker op de gekoppelde skill. */
  huidigNiveau: number | null
  opleidingAanvraagId: string | null
  /** Naam van de opleiding (of de vrije vraag) van de gekoppelde aanvraag. */
  opleidingNaam: string | null
  vervangenDoorId: string | null
  /** De gesprekken waarin dit doel is besproken, oudste eerst. */
  besprekingen: OntwikkelBesprekingDTO[]
  laatstBesprokenOp: string | null
  statusToelichting: string | null
  vervalReden: string | null
  /** Statusvoorstel van de medewerker in de portal, tot iemand het overneemt. */
  voorgesteldeStatus: OntwikkelDoelStatus | null
  voorgesteldOp: string | null
}

/** Een keuze in de sectie: skill met het huidige niveau van deze medewerker. */
export interface OntwikkelSkillOptieDTO {
  id: string
  naam: string
  schaalMin: number
  schaalMax: number
  huidigNiveau: number | null
}

/** Alles wat de sectie Ontwikkeling in het verslagformulier nodig heeft (één bron). */
export interface GesprekOntwikkelingDTO {
  gesprekId: string
  employeeId: string
  employeeNaam: string
  modus: GesprekSectieModus
  magBewerken: boolean
  /** Mag de interne inschatting zien en vastleggen. */
  magInschatting: boolean
  /** De actuele doorgroeiwens (of null). */
  wens: OntwikkelDoelDTO | null
  /** Is de wens in DIT gesprek vastgelegd of bevestigd? Voedt de vrijgeefregel. */
  wensBesprokenInGesprek: boolean
  /** Eerdere versies, nieuwste eerst. */
  wensHistorie: OntwikkelDoelDTO[]
  /** Open doelen plus wat in dit gesprek is behandeld. */
  doelen: OntwikkelDoelDTO[]
  /** Is er in dit gesprek iets aan de sectie gedaan? */
  ingevuld: boolean
  /** Het vorige afgeronde gesprek sloeg ontwikkeling over (datum), anders null. */
  vorigGesprekNietBesproken: string | null
  opties: {
    /** Doelfuncties van de ontwikkelpaden plus de functiegroepen uit de functiematrix. */
    richtingen: string[]
    skills: OntwikkelSkillOptieDTO[]
    opleidingen: Array<{ id: string; naam: string }>
  }
}

/** Portal: de eigen, niet-interne doelen (geen eigenaar-id's of interne velden). */
export interface MijnOntwikkelDoelDTO {
  id: string
  soort: OntwikkelDoelSoort
  omschrijving: string
  streefdatum: string | null
  status: OntwikkelDoelStatus
  vastgelegdOp: string
  // ── batch-71 gesprek-secties (FO-536 §3.A3, §5) - nooit de interne inschatting ──
  richting: string | null
  termijn: OntwikkelDoelTermijn | null
  toelichtingMedewerker: string | null
  /** yyyy-mm-dd van het laatste vrijgegeven gesprek waarin dit is besproken, anders null. */
  besprokenOp: string | null
  voorgesteldeStatus: OntwikkelDoelStatus | null
}

export interface MijnOntwikkelingDTO {
  doelen: MijnOntwikkelDoelDTO[]
  /** De laatst vastgelegde doorgroeiwens (of null). */
  doorgroeiwens: MijnOntwikkelDoelDTO | null
}

/** Portal: de medewerker stelt een status voor; de leidinggevende neemt hem over. */
export const mijnOntwikkelStatusVoorstelSchema = z.object({
  status: z.enum(['BEZIG', 'BEHAALD'], { error: 'Kies een status' }),
})
export type MijnOntwikkelStatusVoorstelInput = z.infer<typeof mijnOntwikkelStatusVoorstelSchema>

// ─── HRM > Ontwikkeling (FO-536 §3.B) ────────────────────────────────────────

export const hrmOntwikkelingQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  zoek: z.string().trim().max(100).optional(),
  departmentId: KALE_ID.optional(),
  functie: z.string().trim().max(200).optional(),
  termijn: z.enum(ONTWIKKEL_DOEL_TERMIJNEN).optional(),
  status: z.enum(ONTWIKKEL_DOEL_STATUSSEN).optional(),
  /** Alleen medewerkers met een open doel dat langer dan 6 maanden niet is besproken. */
  zonderOpvolging: queryBoolean().optional(),
})
export type HrmOntwikkelingQuery = z.infer<typeof hrmOntwikkelingQuerySchema>

export interface HrmOntwikkelingRijDTO {
  employeeId: string
  naam: string
  functie: string | null
  afdeling: string | null
  wensRichting: string | null
  wensTermijn: OntwikkelDoelTermijn | null
  /** INTERN: alleen met MEDEWERKERS.canEdit of HR; anders null. */
  wensInschatting: OntwikkelDoelInschatting | null
  openDoelen: number
  zonderOpvolging: number
  behaaldDitJaar: number
  opleidingsaanvragen: number
  /** yyyy-mm-dd van de laatste bespreking van een doel of wens. */
  laatsteBespreking: string | null
}

export interface HrmOntwikkelingKpiDTO {
  medewerkersMetWens: number
  openDoelen: number
  zonderOpvolging: number
  behaaldDitJaar: number
}

export interface HrmOntwikkelingOverzichtDTO {
  rijen: HrmOntwikkelingRijDTO[]
  kpi: HrmOntwikkelingKpiDTO
  magInschatting: boolean
}
